import React, { useState } from "react";
import { Wand2, Copy, Check } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface Suggestion {
  title: string;
  type: "listen" | "fence";
  description: string;
  rule_text: string;
}

export const RuleSuggestions: React.FC = () => {
  const { session } = useAuth();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<number | null>(null);

  const analyze = async () => {
    if (!session?.access_token || loading) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("suggest-rules", {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      setSuggestions(data.suggestions ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to get suggestions");
    } finally {
      setLoading(false);
    }
  };

  const copyRule = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(index);
      toast.success("Rule text copied to clipboard");
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  return (
    <Card className="border border-[#E5E7EB]">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold text-[#1A2E4A] flex items-center gap-2">
            <Wand2 className="h-4 w-4 text-[#1A2E4A]" />
            AI Rule Suggestions
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={analyze}
            disabled={loading}
            className="text-xs"
          >
            {loading ? (
              <Wand2 className="h-3 w-3 animate-spin mr-1" />
            ) : (
              <Wand2 className="h-3 w-3 mr-1" />
            )}
            {loading ? "Analyzing..." : "Analyze my feed"}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <p className="text-sm text-red-500">{error}</p>}
        {suggestions.length === 0 && !loading && (
          <p className="text-sm text-muted-foreground">
            Let Claude analyze your feed patterns and suggest rules you might be missing.
          </p>
        )}
        {suggestions.map((s, i) => (
          <div key={i} className="border border-[#E5E7EB] rounded-md p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-[#1A2E4A]">{s.title}</span>
              <Badge
                variant="secondary"
                className={`text-xs ${s.type === "listen" ? "bg-green-100 text-green-800 border-green-200" : "bg-gray-100 text-gray-700 border-gray-200"}`}
              >
                {s.type === "listen" ? "Listen" : "Fence"}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">{s.description}</p>
            <div className="flex items-start gap-2">
              <code className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded px-2 py-1 font-mono break-all">
                {s.rule_text}
              </code>
              <Button
                size="sm"
                variant="ghost"
                className="shrink-0 h-7 px-2"
                onClick={() => copyRule(s.rule_text, i)}
              >
                {copied === i ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default RuleSuggestions;
